// src/components/BalanceReportScreen.jsx
// Relatório de balanço premium.
// Rota: /reports/balance
// Endpoint: GET /psy/finance/balance?id=ID_USUARIO
// UI isolada em .bal-* para não mexer em .dash-* global.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";


import AppSidebar from "./AppSidebar";
import Logo from "../assets/logoClaro.webp";
import "../sass/pages/balance-report.scss";
import { API_BASE as CONFIG_API_BASE } from "../config/apiBase";

const API_BASE = (
  CONFIG_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const TX_FILTERS = [
  { value: "todas", label: "Todas" },
  { value: "receita", label: "Receitas" },
  { value: "despesa", label: "Despesas" },
];

function readStorage(keys = []) {
  for (const key of keys) {
    const value = sessionStorage.getItem(key) || localStorage.getItem(key);

    if (value && String(value).trim() !== "") {
      return value;
    }
  }

  return "";
}

function saveAuthToken(token) {
  if (!token) return;

  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function brl(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function brlSigned(value, isIncome) {
  const number = safeNumber(value);
  const signal = isIncome ? "+" : "−";

  return `${signal}${brl(Math.abs(number))}`;
}

function percentBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(safeNumber(value));
}

function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);

  if (!date) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function getTimeBR(value) {
  const date = parseDate(value);

  if (!date) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getFirstName(name = "") {
  const clean = String(name || "").trim();

  if (!clean) return "";

  return clean.split(/\s+/)[0];
}

function getMonthLabel(value, index = 0) {
  const month = Number(value);

  if (Number.isFinite(month)) {
    if (month >= 1 && month <= 12) return MONTHS[month - 1];
    if (month >= 0 && month <= 11) return MONTHS[month];
  }

  return MONTHS[index % 12];
}

function getTransactionType(item = {}) {
  const raw = normalizeText(item?.tipo || item?.type || item?.categoria || "");

  if (raw.includes("receita") || raw.includes("entrada") || raw.includes("income")) {
    return "receita";
  }

  return "despesa";
}

function getTransactionValue(item = {}) {
  return safeNumber(item?.valor ?? item?.value ?? item?.total ?? 0);
}

function getTransactionDescription(item = {}) {
  return String(item?.descricao || item?.description || item?.categoria || "Transação").trim();
}

function getTransactionDate(item = {}) {
  return item?.data || item?.createdAt || item?.dataCadastro || null;
}

function normalizeTransactions(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows.slice(0, 60).map((item, index) => {
    const tipo = getTransactionType(item);
    const valor = getTransactionValue(item);

    return {
      id: item?._id || item?.id || `${item?.data || "tx"}-${index}`,
      tipo,
      descricao: getTransactionDescription(item),
      valor: Math.abs(valor),
      data: getTransactionDate(item),
      categoria: item?.categoria || item?.category || "",
    };
  });
}

function normalizeBars(data = []) {
  const rows = Array.isArray(data) ? data : [];

  return rows
    .slice(0, 24)
    .map((item, index) => ({
      label: item?.label || item?.nome || getMonthLabel(item?.mes ?? item?.month, index),
      mes: item?.mes ?? item?.month ?? index,
      valor: safeNumber(item?.valor ?? item?.saldo ?? item?.total ?? 0),
    }))
    .filter((item) => Number.isFinite(item.valor));
}

function buildTransactionSearchText(transaction) {
  return normalizeText(
    [
      transaction.tipo,
      transaction.descricao,
      transaction.categoria,
      brl(transaction.valor),
      formatDateBR(transaction.data),
    ]
      .filter(Boolean)
      .join(" ")
  );
}

async function readApiError(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      return payload?.message || payload?.error || JSON.stringify(payload);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function getBalanceHealth(saldo, receita, despesa) {
  const saldoNumber = safeNumber(saldo);
  const receitaNumber = safeNumber(receita);
  const despesaNumber = safeNumber(despesa);

  if (receitaNumber <= 0 && despesaNumber <= 0) {
    return {
      label: "Sem movimento",
      tone: "neutral",
      icon: "i",
      description: "Ainda não há dados suficientes para leitura do balanço.",
    };
  }

  if (saldoNumber < 0) {
    return {
      label: "Negativo",
      tone: "bad",
      icon: "!",
      description: "As despesas superaram as receitas no período.",
    };
  }

  if (saldoNumber === 0) {
    return {
      label: "Equilibrado",
      tone: "warning",
      icon: "≋",
      description: "Receitas e despesas estão praticamente empatadas.",
    };
  }

  return {
    label: "Positivo",
    tone: "good",
    icon: "✓",
    description: "O período fechou com saldo favorável.",
  };
}

function KpiCard({ icon, label, value, description, tone = "blue" }) {
  return (
    <article className={`bal-kpi bal-kpi--${tone}`}>
      <div className="bal-kpi__icon" aria-hidden="true">
        {icon}
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{description}</small>
      </div>
    </article>
  );
}

function DonutChart({ receita, despesa }) {
  const total = safeNumber(receita) + safeNumber(despesa);
  const receitaPercent = total > 0 ? (safeNumber(receita) / total) * 100 : 0;
  const despesaPercent = total > 0 ? (safeNumber(despesa) / total) * 100 : 0;

  return (
    <div className="bal-pie">
      <div className="bal-donut" role="img" aria-label="Distribuição de receitas e despesas">
        <div
          className="bal-donut__ring"
          style={{
            background: total
              ? `conic-gradient(var(--bal-green-500) 0 ${receitaPercent}%, var(--bal-red-500) ${receitaPercent}% 100%)`
              : "conic-gradient(var(--bal-slate-200) 0 100%)",
          }}
        />

        <div className="bal-donut__hole">
          <strong>{brl(total)}</strong>
          <span>Receitas + despesas</span>
        </div>
      </div>

      <div className="bal-legend">
        <LegendRow
          color="green"
          label="Receitas"
          value={brl(receita)}
          percent={`${percentBR(receitaPercent)}%`}
        />

        <LegendRow
          color="red"
          label="Despesas"
          value={brl(despesa)}
          percent={`${percentBR(despesaPercent)}%`}
        />
      </div>
    </div>
  );
}

function LegendRow({ color, label, value, percent }) {
  return (
    <div className="bal-legend__row">
      <span className={`bal-dot bal-dot--${color}`} aria-hidden="true" />
      <div>
        <strong>{label}</strong>
        <small>{percent}</small>
      </div>
      <span>{value}</span>
    </div>
  );
}

function BalanceBars({ data = [] }) {
  const rows = normalizeBars(data);

  if (!rows.length) {
    return (
      <div className="bal-chart-empty">
        <span>⚖️</span>
        <strong>Sem saldo mensal.</strong>
        <small>Quando houver dados mensais, o gráfico aparecerá aqui.</small>
      </div>
    );
  }

  const visibleRows = rows.slice(0, 12);
  const absMax = Math.max(...visibleRows.map((item) => Math.abs(item.valor)), 1);

  return (
    <div className="bal-bars" role="img" aria-label="Evolução do saldo mensal">
      <div className="bal-bars__y" aria-hidden="true">
        <span>{brl(absMax)}</span>
        <span>{brl(0)}</span>
        <span>{brl(-absMax)}</span>
      </div>

      <div className="bal-bars__plot">
        {visibleRows.map((item, index) => {
          const value = safeNumber(item.valor);
          const height = Math.max(8, Math.round((Math.abs(value) / absMax) * 120));
          const isPositive = value >= 0;

          return (
            <div
              className="bal-bars__col"
              key={`${item.label}-${index}`}
              title={`${item.label}: ${brl(value)}`}
            >
              <div className="bal-bars__stack">
                <span className="bal-bars__zero" />
                <span
                  className={`bal-bars__bar ${isPositive ? "is-pos" : "is-neg"}`}
                  style={{ height: `${height}px` }}
                />
              </div>

              <span className="bal-bars__label">{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BalanceSkeleton() {
  return (
    <div className="bal-skeleton" aria-hidden="true">
      <div className="bal-skeleton__hero" />
      <div className="bal-skeleton__kpis">
        <span />
        <span />
        <span />
      </div>
      <div className="bal-skeleton__grid">
        <span />
        <span />
      </div>
      <div className="bal-skeleton__list">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function EmptyState({ title, message, onRetry, onBack, isError = false }) {
  return (
    <section className={`bal-state ${isError ? "bal-state--error" : ""}`}>
      <div className="bal-state__icon">{isError ? "!" : "⚖️"}</div>
      <strong>{title}</strong>
      <span>{message}</span>

      <div className="bal-state__actions">
        <button className="bal-btn bal-btn--ghost-dark" type="button" onClick={onBack}>
          Voltar
        </button>

        <button className="bal-btn bal-btn--primary" type="button" onClick={onRetry}>
          Tentar novamente
        </button>
      </div>
    </section>
  );
}

export default function BalanceReportScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [txFilter, setTxFilter] = useState("todas");
  const [search, setSearch] = useState("");

  const nomeUsuario = useMemo(() => readStorage([AUTH_KEYS.USER_NAME]) || "", []);
  const firstName = useMemo(() => getFirstName(nomeUsuario), [nomeUsuario]);

  const authOrRedirect = useCallback(() => {
    const token = readStorage([AUTH_KEYS.TOKEN, "token"]);
    const userId = readStorage([AUTH_KEYS.USER_ID, "id"]);

    if (!token || !userId) {
      setError("Sessão expirada. Faça login novamente.");
      return null;
    }

    return { token, userId };
  }, []);

  const fetchBalanceReport = useCallback(
    async ({ signal, silent = false } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const response = await fetch(
          `${API_BASE}/psy/finance/balance?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
            signal,
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          setError("Sessão expirada. Faça login novamente.");
          setReport(null);
          return;
        }

        if (!response.ok) {
          const message = await readApiError(response);
          setError(message || "Erro ao carregar relatório.");
          setReport(null);
          return;
        }

        const payload = await response.json().catch(() => null);

        setReport(payload || null);
        setLastUpdatedAt(new Date());
      } catch (err) {
        if (err?.name === "AbortError") return;

        console.error("Erro ao carregar relatório de balanço:", err);
        setError("Falha de comunicação com a API.");
        setReport(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchBalanceReport({ signal: controller.signal });

    return () => controller.abort();
  }, [fetchBalanceReport]);

  const receita = safeNumber(report?.receita ?? report?.totalReceita ?? 0);
  const despesa = safeNumber(report?.despesa ?? report?.totalDespesa ?? 0);
  const saldo = safeNumber(report?.saldo ?? report?.resultado ?? receita - despesa);

  const distribution = useMemo(() => {
    const total = receita + despesa;

    return {
      total,
      receitaPercent: total > 0 ? (receita / total) * 100 : 0,
      despesaPercent: total > 0 ? (despesa / total) * 100 : 0,
    };
  }, [receita, despesa]);

  const monthlyBalance = useMemo(
    () => normalizeBars(report?.saldoMensal || report?.evolucaoMensal || []),
    [report]
  );

  const transactions = useMemo(
    () => normalizeTransactions(report?.ultimasTransacoes || report?.transactions || []),
    [report]
  );

  const filteredTransactions = useMemo(() => {
    const query = normalizeText(search);

    return transactions.filter((transaction) => {
      const matchesFilter = txFilter === "todas" || transaction.tipo === txFilter;
      const matchesSearch = !query || buildTransactionSearchText(transaction).includes(query);

      return matchesFilter && matchesSearch;
    });
  }, [transactions, txFilter, search]);

  const filteredTotal = useMemo(() => {
    return filteredTransactions.reduce((sum, transaction) => {
      const signal = transaction.tipo === "receita" ? 1 : -1;
      return sum + signal * safeNumber(transaction.valor);
    }, 0);
  }, [filteredTransactions]);

  const health = useMemo(
    () => getBalanceHealth(saldo, receita, despesa),
    [saldo, receita, despesa]
  );

  const reload = () => fetchBalanceReport({ silent: true });
  const goBack = () => navigate("/reports");
  const goLogin = () => navigate("/login");
  const goFinance = () => navigate("/finance");
  const printReport = () => window.print();

  return (
    <div className="bal-page">
      <div className="bal-bg" aria-hidden="true">
        <span className="bal-bg__orb bal-bg__orb--one" />
        <span className="bal-bg__orb bal-bg__orb--two" />
        <span className="bal-bg__orb bal-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="bal-topbar">
        <button
          className="bal-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>

        <div className="bal-brand">
          <img src={Logo} alt="PsyAssist" className="bal-brand__logo" />
          <div className="bal-brand__text">
            <strong>Relatório de balanço</strong>
            <span>Receitas, despesas e saldo • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="bal-top-actions">
          <button
            className="bal-btn bal-btn--ghost bal-btn--desktop"
            type="button"
            onClick={goBack}
          >
            Voltar
          </button>

          <button
            className="bal-btn bal-btn--ghost"
            type="button"
            onClick={printReport}
            disabled={loading || Boolean(error) || !report}
          >
            Imprimir
          </button>

          <button
            className="bal-btn bal-btn--primary"
            type="button"
            onClick={reload}
            disabled={loading || refreshing}
          >
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="bal-main">
        {loading ? (
          <BalanceSkeleton />
        ) : error ? (
          <EmptyState
            title="Não foi possível carregar o balanço."
            message={error}
            onRetry={String(error).includes("Sessão") ? goLogin : reload}
            onBack={goBack}
            isError
          />
        ) : !report ? (
          <EmptyState
            title="Nenhum dado encontrado."
            message="Ainda não há movimentações suficientes para montar o balanço."
            onRetry={reload}
            onBack={goBack}
          />
        ) : (
          <>
            <section className="bal-hero">
              <div>
                <span className="bal-kicker">Visão consolidada</span>
                <h1>Balanço</h1>
                <p>
                  {firstName ? `${firstName}, ` : ""}
                  veja o comparativo entre receitas e despesas, a evolução do
                  saldo mensal e as últimas movimentações do consultório.
                </p>
              </div>

              <aside className={`bal-hero-card bal-hero-card--${health.tone}`}>
                <div className="bal-hero-card__icon" aria-hidden="true">
                  {health.icon}
                </div>

                <div>
                  <small>Status do período</small>
                  <strong>{health.label}</strong>
                  <span>{health.description}</span>
                </div>
              </aside>
            </section>

            <section className="bal-kpis">
              <KpiCard
                icon="📈"
                label="Receitas"
                value={brl(receita)}
                description={`${percentBR(distribution.receitaPercent)}% do movimento`}
                tone="green"
              />

              <KpiCard
                icon="📉"
                label="Despesas"
                value={brl(despesa)}
                description={`${percentBR(distribution.despesaPercent)}% do movimento`}
                tone="red"
              />

              <KpiCard
                icon="💼"
                label="Saldo"
                value={brl(saldo)}
                description="Receitas menos despesas"
                tone={saldo >= 0 ? "blue" : "red"}
              />
            </section>

            <section className="bal-grid2">
              <div className="bal-panel">
                <div className="bal-panel__head">
                  <div>
                    <span>Distribuição</span>
                    <h2>Receitas × despesas</h2>
                    <p>Proporção entre entradas e saídas no período analisado.</p>
                  </div>
                </div>

                <DonutChart receita={receita} despesa={despesa} />
              </div>

              <div className="bal-panel">
                <div className="bal-panel__head">
                  <div>
                    <span>Evolução</span>
                    <h2>Saldo mensal</h2>
                    <p>Leitura dos meses positivos e negativos.</p>
                  </div>

                  {lastUpdatedAt ? (
                    <div className="bal-updated">
                      Atualizado às {getTimeBR(lastUpdatedAt)}
                    </div>
                  ) : null}
                </div>

                <BalanceBars data={monthlyBalance} />
              </div>
            </section>

            <section className="bal-panel">
              <div className="bal-panel__head bal-panel__head--with-tools">
                <div>
                  <span>Movimentações</span>
                  <h2>Últimas transações</h2>
                  <p>Filtre as últimas entradas e saídas do relatório.</p>
                </div>

                <div className="bal-counter">
                  {filteredTransactions.length} de {transactions.length}
                </div>
              </div>

              <div className="bal-toolbar">
                <label className="bal-search">
                  <span aria-hidden="true">⌕</span>
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Buscar transação..."
                  />

                  {search ? (
                    <button type="button" onClick={() => setSearch("")} aria-label="Limpar busca">
                      ×
                    </button>
                  ) : null}
                </label>

                <div className="bal-filter-group" aria-label="Filtro de transações">
                  {TX_FILTERS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={txFilter === option.value ? "is-active" : ""}
                      onClick={() => setTxFilter(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className={`bal-total-chip ${filteredTotal >= 0 ? "is-pos" : "is-neg"}`}>
                  <span>Saldo filtrado</span>
                  <strong>{brl(filteredTotal)}</strong>
                </div>
              </div>

              {filteredTransactions.length === 0 ? (
                <div className="bal-empty">
                  Nenhuma transação encontrada com os filtros atuais.
                </div>
              ) : (
                <div className="bal-list">
                  {filteredTransactions.map((transaction) => {
                    const isIncome = transaction.tipo === "receita";

                    return (
                      <article className={`bal-row ${isIncome ? "is-pos" : "is-neg"}`} key={transaction.id}>
                        <div className={`bal-row__icon ${isIncome ? "is-pos" : "is-neg"}`} aria-hidden="true">
                          {isIncome ? "📈" : "📉"}
                        </div>

                        <div className="bal-row__content">
                          <strong>{transaction.descricao || "Transação"}</strong>
                          <span>
                            {formatDateBR(transaction.data)}
                            {transaction.categoria ? ` • ${transaction.categoria}` : ""}
                          </span>
                        </div>

                        <div className={`bal-row__value ${isIncome ? "is-pos" : "is-neg"}`}>
                          {brlSigned(transaction.valor, isIncome)}
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="bal-bottom-actions">
              <button className="bal-btn bal-btn--ghost-dark" type="button" onClick={goBack}>
                Voltar aos relatórios
              </button>

              <button className="bal-btn bal-btn--primary" type="button" onClick={goFinance}>
                Abrir financeiro
              </button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
